import { useState } from 'react';
import { ArrowLeftIcon, PlusIcon } from 'lucide-react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import type {
  CustomDashboardTabGroup,
  ManagedCustomDashboardTab,
  ManagedCustomDashboardTabsConfig,
} from '@/lib/api/custom-dashboard';
import {
  deleteGroupRequest,
  deleteTabRequest,
  layoutFromConfig,
  layoutRequest,
  moveGroup,
  moveTab,
  moveTabToGroup,
} from '@/lib/custom-dashboard/tab-layout';
import { AddTabGroupForm } from './AddTabGroupForm';
import type { CancelRequestTarget, DataUpdateRequestTarget } from './dataUpdateTargets';
import { useTabLayoutActions } from './hooks/useTabLayoutActions';
import { TabGroupCard } from './TabGroupCard';
import { TabRow } from './TabRow';
import type { TabMoveTarget } from './TabRowMenus';
import { UngroupedTabsCard } from './UngroupedTabsCard';

/** アップロード先（既存タブ、または新規タブ。新規タブの group が null ならグループ未指定に作る）。 */
export type UploadTarget =
  | { kind: 'existing'; tabId: string; name: string }
  | { kind: 'new'; group: CustomDashboardTabGroup | null };

/** 「移動先」でグループ未指定を選んだときの値（Select の値は空文字にできないため）。 */
const UNGROUPED_MOVE_VALUE = '__ungrouped__';

export interface HistoryTarget {
  tabId: string;
  name: string;
}

/** 行から開くプレビューの対象（公開前の revision など）。 */
export interface PreviewRequestTarget {
  tabId: string;
  name: string;
  revision: number;
}

export interface TabLayoutEditorProps {
  accountId: string;
  authHeaders: Record<string, string>;
  /** 見出しに出すアカウント名（どのアカウントの設定かを示す）。 */
  accountName?: string;
  config: ManagedCustomDashboardTabsConfig;
  /** ダッシュボード表示に戻る。表示できるタブが無いとき（設定が必須）は渡さない。 */
  onClose?: () => void;
  onUploadRequest: (target: UploadTarget) => void;
  onHistoryRequest: (target: HistoryTarget) => void;
  /** 定期更新の設定ダイアログを開く（対象は履歴と同じくタブの ID と名前）。 */
  onUpdateSettingsRequest: (target: HistoryTarget) => void;
  onPreviewRequest: (target: PreviewRequestTarget) => void;
  onDataUpdateRequest: (target: DataUpdateRequestTarget) => void;
  onCancelRequest: (target: CancelRequestTarget) => void;
  isCancelling: boolean;
}

type Editing = { kind: 'tab' | 'group'; id: string } | null;
type DeleteTarget =
  | { kind: 'tab'; tab: ManagedCustomDashboardTab }
  | { kind: 'group'; group: CustomDashboardTabGroup }
  | null;

/**
 * タブグループとタブの配置を編集する。グループの追加・名前変更・並べ替え・削除と、タブの追加・名前変更・並べ替え・
 * グループ間の移動・削除を、操作ごとにその場で保存する（下書きを持たないため、ほかの操作で未保存の変更が消えない）。
 * グループ未指定のタブは「グループ未指定」欄に出し、グループに入れるまで閲覧画面に出ない。
 */
export function TabLayoutEditor({
  accountId,
  accountName,
  authHeaders,
  config,
  onClose,
  onUploadRequest,
  onHistoryRequest,
  onUpdateSettingsRequest,
  onPreviewRequest,
  onDataUpdateRequest,
  onCancelRequest,
  isCancelling,
}: TabLayoutEditorProps) {
  const actions = useTabLayoutActions(accountId, authHeaders);
  const [editing, setEditing] = useState<Editing>(null);
  const [editingName, setEditingName] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget>(null);

  const layout = layoutFromConfig(config);
  const tabById = new Map(config.tabs.map((tab) => [tab.tabId, tab]));
  const groupById = new Map(config.groups.map((group) => [group.groupId, group]));
  const tabsOf = (tabIds: string[]) =>
    tabIds
      .map((tabId) => tabById.get(tabId))
      .filter((tab): tab is ManagedCustomDashboardTab => tab !== undefined);

  const startRename = (kind: 'tab' | 'group', id: string, name: string) => {
    setEditing({ kind, id });
    setEditingName(name);
  };

  const confirmRename = async () => {
    const name = editingName.trim();
    if (editing === null || name.length === 0) return;
    const saved =
      editing.kind === 'tab'
        ? await actions.renameTab(editing.id, name)
        : await actions.renameGroup(editing.id, name);
    if (saved) setEditing(null);
  };

  const confirmDelete = () => {
    const target = deleteTarget;
    setDeleteTarget(null);
    if (target === null) return;
    if (target.kind === 'tab') {
      void actions.saveLayout(deleteTabRequest(layout, target.tab.tabId), 'タブを削除しました');
    } else {
      void actions.saveLayout(
        deleteGroupRequest(layout, target.group.groupId),
        'タブグループを削除しました',
      );
    }
  };

  const isEditingTab = (tabId: string) => editing?.kind === 'tab' && editing.id === tabId;
  const isEditingGroup = (groupId: string) => editing?.kind === 'group' && editing.id === groupId;
  const tabCountOfGroup = (groupId: string) =>
    layout.groups.find((group) => group.groupId === groupId)?.tabIds.length ?? 0;

  const moveTargetsFor = (currentGroupId: string | null): TabMoveTarget[] => [
    ...config.groups
      .filter((group) => group.groupId !== currentGroupId)
      .map((group) => ({ value: group.groupId, label: group.name })),
    ...(currentGroupId === null ? [] : [{ value: UNGROUPED_MOVE_VALUE, label: 'グループ未指定' }]),
  ];

  const moveToGroup = (tabId: string, value: string) => {
    const targetGroupId = value === UNGROUPED_MOVE_VALUE ? null : value;
    const label =
      targetGroupId === null ? 'グループ未指定' : (groupById.get(targetGroupId)?.name ?? '');
    void actions.saveLayout(
      layoutRequest(moveTabToGroup(layout, tabId, targetGroupId)),
      `タブを「${label}」へ移動しました`,
    );
  };

  /** タブ 1 行。`groupId` が null のタブ（グループ未指定）は順を持たないため並べ替えを出さない。 */
  const renderTabRow = (
    tab: ManagedCustomDashboardTab,
    groupId: string | null,
    index: number,
    count: number,
  ) => (
    <TabRow
      key={tab.tabId}
      tab={tab}
      index={index}
      canReorder={groupId !== null}
      isFirst={index === 0}
      isLast={index === count - 1}
      moveTargets={moveTargetsFor(groupId)}
      isLayoutBusy={actions.isBusy}
      isEditing={isEditingTab(tab.tabId)}
      editingName={editingName}
      isRenaming={actions.isBusy}
      onEditingNameChange={setEditingName}
      onStartRename={() => startRename('tab', tab.tabId, tab.name)}
      onRenameConfirm={() => void confirmRename()}
      onRenameCancel={() => setEditing(null)}
      onMoveUp={() =>
        void actions.saveLayout(
          layoutRequest(moveTab(layout, tab.tabId, -1)),
          'タブを並べ替えました',
        )
      }
      onMoveDown={() =>
        void actions.saveLayout(
          layoutRequest(moveTab(layout, tab.tabId, 1)),
          'タブを並べ替えました',
        )
      }
      onMoveToGroup={(value) => moveToGroup(tab.tabId, value)}
      onUploadRequest={() =>
        onUploadRequest({ kind: 'existing', tabId: tab.tabId, name: tab.name })
      }
      onHistoryRequest={() => onHistoryRequest({ tabId: tab.tabId, name: tab.name })}
      onUpdateSettingsRequest={() => onUpdateSettingsRequest({ tabId: tab.tabId, name: tab.name })}
      onPreviewRequest={(revision) =>
        onPreviewRequest({ tabId: tab.tabId, name: tab.name, revision })
      }
      onDeleteRequest={() => setDeleteTarget({ kind: 'tab', tab })}
      onDataUpdateRequest={onDataUpdateRequest}
      onCancelRequest={onCancelRequest}
      isCancellingDataUpdate={isCancelling}
    />
  );

  return (
    <div className="flex justify-center px-6 pb-14 pt-8">
      <div className="w-full max-w-6xl space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            {onClose && (
              <Button
                variant="ghost"
                size="sm"
                className="-ml-2 h-8 px-2 text-muted-foreground"
                onClick={onClose}
                disabled={actions.isBusy}
              >
                <ArrowLeftIcon size={16} aria-hidden />
                ダッシュボードに戻る
              </Button>
            )}
            <h1 className="mt-2 text-2xl font-semibold tracking-tight">タブ設定</h1>
            <p className="mt-1.5 text-sm text-muted-foreground">
              {accountName ? `${accountName}（${accountId}）の` : ''}
              タブグループとタブを管理します。変更はその場で保存されます
            </p>
          </div>
          {/* グループ未指定のタブを作る。グループに入れるタブはグループのカードから追加する */}
          <Button variant="outline" onClick={() => onUploadRequest({ kind: 'new', group: null })}>
            <PlusIcon size={16} aria-hidden />
            タブを追加
          </Button>
        </div>

        {layout.ungroupedTabIds.length > 0 && (
          <UngroupedTabsCard tabCount={layout.ungroupedTabIds.length}>
            {tabsOf(layout.ungroupedTabIds).map((tab, index, tabs) =>
              renderTabRow(tab, null, index, tabs.length),
            )}
          </UngroupedTabsCard>
        )}

        {layout.groups.length === 0 && (
          <p className="text-sm text-muted-foreground">
            タブグループがありません。タブグループを追加し、タブを入れると閲覧画面に表示されます。
          </p>
        )}

        {layout.groups.map((layoutGroup, groupIndex) => {
          const group = groupById.get(layoutGroup.groupId);
          if (group === undefined) return null;
          const tabs = tabsOf(layoutGroup.tabIds);
          return (
            <TabGroupCard
              key={group.groupId}
              group={group}
              tabCount={tabs.length}
              isFirst={groupIndex === 0}
              isLast={groupIndex === layout.groups.length - 1}
              isLayoutBusy={actions.isBusy}
              isEditing={isEditingGroup(group.groupId)}
              editingName={editingName}
              isRenaming={actions.isBusy}
              onEditingNameChange={setEditingName}
              onStartRename={() => startRename('group', group.groupId, group.name)}
              onRenameConfirm={() => void confirmRename()}
              onRenameCancel={() => setEditing(null)}
              onMoveUp={() =>
                void actions.saveLayout(
                  layoutRequest(moveGroup(layout, group.groupId, -1)),
                  'タブグループを並べ替えました',
                )
              }
              onMoveDown={() =>
                void actions.saveLayout(
                  layoutRequest(moveGroup(layout, group.groupId, 1)),
                  'タブグループを並べ替えました',
                )
              }
              onDeleteRequest={() => setDeleteTarget({ kind: 'group', group })}
              onAddTabRequest={() => onUploadRequest({ kind: 'new', group })}
            >
              {tabs.map((tab, index) => renderTabRow(tab, group.groupId, index, tabs.length))}
            </TabGroupCard>
          );
        })}

        <AddTabGroupForm disabled={actions.isBusy} onSubmit={actions.createGroup} />

        <AlertDialog
          open={deleteTarget !== null}
          onOpenChange={(open) => {
            if (!open) setDeleteTarget(null);
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {deleteTarget?.kind === 'group'
                  ? `タブグループ「${deleteTarget.group.name}」を削除しますか？`
                  : `タブ「${deleteTarget?.tab.name ?? ''}」を削除しますか？`}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {deleteTarget?.kind === 'group'
                  ? groupDeleteDescription(tabCountOfGroup(deleteTarget.group.groupId))
                  : '削除するとアップロード履歴と成果物も削除され、元に戻せません。データ更新中のタブは削除できません。'}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>キャンセル</AlertDialogCancel>
              <AlertDialogAction onClick={confirmDelete}>削除</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

function groupDeleteDescription(tabCount: number): string {
  return tabCount === 0
    ? '空のタブグループを削除します。'
    : `中のタブ（${tabCount} 件）は「グループ未指定」に移り、グループに入れ直すまで閲覧画面に表示されません。タブ自体は削除されません。`;
}
